
import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { useInternalAuth } from './useInternalAuth';
import { usePoints } from './usePoints';
import { toast } from 'sonner';

export const useDailyBonus = () => {
  const { user: supabaseUser } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const { awardPoints, transactions, refetch, transactionsLoaded } = usePoints();
  const [canClaimDaily, setCanClaimDaily] = useState(false);
  const [timeUntilNextClaim, setTimeUntilNextClaim] = useState<string>('');
  const [isClaimingDaily, setIsClaimingDaily] = useState(false);
  const [hasCheckedBonus, setHasCheckedBonus] = useState(false);

  const user = supabaseUser || internalUser;

  useEffect(() => {
    // Don't evaluate until user exists AND transactions are fully loaded
    if (!user || !transactionsLoaded) {
      setHasCheckedBonus(false);
      return;
    }

    // Safety check: if transactions is null, wait for data
    if (transactions === null) {
      return;
    }

    // Find the most recent daily login bonus transaction
    const lastDailyBonus = transactions.find(
      (transaction) => transaction.transaction_type === 'daily_login'
    );

    if (!lastDailyBonus) {
      setCanClaimDaily(true);
      setTimeUntilNextClaim('');
      setHasCheckedBonus(true);
      return;
    }

    const lastClaimTime = new Date(lastDailyBonus.created_at);
    const now = new Date();
    const timeDifference = now.getTime() - lastClaimTime.getTime();
    const hoursPassedSinceLastClaim = timeDifference / (1000 * 60 * 60);

    // Allow claim if more than 24 hours and 1 minute have passed
    const cooldownHours = 24 + (1/60); // 24 hours and 1 minute
    
    if (hoursPassedSinceLastClaim >= cooldownHours) {
      setCanClaimDaily(true);
      setTimeUntilNextClaim('');
    } else {
      setCanClaimDaily(false);
      
      // Calculate remaining time
      const remainingMs = (cooldownHours * 60 * 60 * 1000) - timeDifference;
      const remainingHours = Math.floor(remainingMs / (1000 * 60 * 60));
      const remainingMinutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));
      
      setTimeUntilNextClaim(`${remainingHours}h ${remainingMinutes}m`);
    }
    
    setHasCheckedBonus(true);
  }, [user, transactions, transactionsLoaded]);

  const claimDailyBonus = async () => {
    if (!user || !canClaimDaily || isClaimingDaily) return;

    setIsClaimingDaily(true);
    
    try {
      await awardPoints(10, 'daily_login', 'Daily login bonus');
      
      // Success - update UI
      toast.success('Daily bonus claimed! +10 points');
      setCanClaimDaily(false);
      setTimeUntilNextClaim('24h 0m');
    } catch (error: any) {
      console.error('Error claiming daily bonus:', error);
      
      // Handle specific error cases
      if (error?.code === 'DUPLICATE_TRANSACTION' || error?.message?.includes('Daily bonus already claimed')) {
        toast.error(error.message || 'Daily bonus already claimed');
        setCanClaimDaily(false);
        // Trigger refetch to get accurate time
        refetch();
      } else {
        toast.error('Failed to claim daily bonus');
      }
    } finally {
      setIsClaimingDaily(false);
    }
  };

  return {
    canClaimDaily: hasCheckedBonus && canClaimDaily,
    timeUntilNextClaim,
    claimDailyBonus,
    isClaimingDaily,
    isLoading: !hasCheckedBonus
  };
};
